import React from 'react'
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Font,
  Svg,
  Path,
  Circle,
  Line,
} from '@react-pdf/renderer'
import { Invoice, VAT_EXEMPTION_REASONS } from '../types/invoice'
import { formatAddress } from '../lib/address'
import { SupportedLanguage } from '../lib/countries'

export type InvoiceLanguage = 'es' | 'en' | 'ru' | 'de' | 'fr'

// Register Inter font with full Unicode support (Latin + Cyrillic + extended)
Font.register({
  family: 'Inter',
  fonts: [
    {
      src: '/fonts/Inter-Regular.ttf',
      fontWeight: 'normal',
    },
    {
      src: '/fonts/Inter-Bold.ttf',
      fontWeight: 'bold',
    },
  ],
})

const translations: Record<InvoiceLanguage, {
  invoice: string
  invoiceNumber: string
  date: string
  from: string
  billTo: string
  nif: string
  description: string
  quantity: string
  unitPrice: string
  retention: string
  vat: string
  total: string
  subtotal: string
  vatAmount: string
  retentionAmount: string
  paymentDetails: string
  iban: string
  swift: string
  bank: string
  accountHolder: string
  reference: string
  vatExemption: string
  notes: string
  thankYou: string
  phone: string
  email: string
  address: string
  vatExemptions: Record<string, string>
}> = {
  es: {
    invoice: 'FACTURA',
    invoiceNumber: 'N',
    date: 'Fecha',
    from: 'De',
    billTo: 'Para',
    nif: 'NIF/CIF',
    description: 'Concepto',
    quantity: 'Uds',
    unitPrice: 'Precio',
    retention: 'Ret.',
    vat: 'IVA',
    total: 'Total',
    subtotal: 'Subtotal',
    vatAmount: 'IVA',
    retentionAmount: 'Retenciones',
    paymentDetails: 'Datos de pago',
    iban: 'IBAN',
    swift: 'SWIFT',
    bank: 'Banco',
    accountHolder: 'Titular',
    reference: 'Ref',
    vatExemption: 'Exencion IVA',
    notes: 'Notas',
    thankYou: 'Gracias por confiar en nosotros',
    phone: 'Tel',
    email: 'Email',
    address: 'Direccion',
    vatExemptions: {
      art21: 'Exportaciones de bienes',
      art21_2: 'Servicios relacionados con exportaciones',
      art22: 'Operaciones asimiladas a exportaciones',
      art25: 'Entregas intracomunitarias',
      art69: 'Servicios a empresarios fuera de Espana',
      art20_18: 'Operaciones financieras',
      art20_9: 'Servicios de ensenanza',
      art20_3: 'Asistencia sanitaria',
      art20: 'Exenciones interiores',
    },
  },
  en: {
    invoice: 'INVOICE',
    invoiceNumber: '#',
    date: 'Date',
    from: 'From',
    billTo: 'Bill to',
    nif: 'Tax ID',
    description: 'Description',
    quantity: 'Qty',
    unitPrice: 'Price',
    retention: 'With.',
    vat: 'VAT',
    total: 'Total',
    subtotal: 'Subtotal',
    vatAmount: 'VAT',
    retentionAmount: 'Withholding',
    paymentDetails: 'Payment details',
    iban: 'IBAN',
    swift: 'SWIFT',
    bank: 'Bank',
    accountHolder: 'Account holder',
    reference: 'Ref',
    vatExemption: 'VAT Exemption',
    notes: 'Notes',
    thankYou: 'Thank you for your business',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    vatExemptions: {
      art21: 'Export of goods',
      art21_2: 'Services related to exports',
      art22: 'Operations assimilated to exports',
      art25: 'Intra-community deliveries',
      art69: 'Services to businesses outside Spain',
      art20_18: 'Financial operations',
      art20_9: 'Education services',
      art20_3: 'Healthcare services',
      art20: 'Interior exemptions',
    },
  },
  ru: {
    invoice: 'СЧЕТ',
    invoiceNumber: 'N',
    date: 'Дата',
    from: 'От',
    billTo: 'Кому',
    nif: 'ИНН',
    description: 'Описание',
    quantity: 'Кол',
    unitPrice: 'Цена',
    retention: 'Уд.',
    vat: 'НДС',
    total: 'Итого',
    subtotal: 'Сумма',
    vatAmount: 'НДС',
    retentionAmount: 'Удержания',
    paymentDetails: 'Реквизиты',
    iban: 'IBAN',
    swift: 'SWIFT',
    bank: 'Банк',
    accountHolder: 'Получатель',
    reference: 'Назн',
    vatExemption: 'Освобождение от НДС',
    notes: 'Примечания',
    thankYou: 'Спасибо за сотрудничество',
    phone: 'Тел',
    email: 'Email',
    address: 'Адрес',
    vatExemptions: {
      art21: 'Экспорт товаров',
      art21_2: 'Услуги, связанные с экспортом',
      art22: 'Операции, приравненные к экспорту',
      art25: 'Внутриобщинные поставки',
      art69: 'Услуги для бизнеса вне Испании',
      art20_18: 'Финансовые операции',
      art20_9: 'Образовательные услуги',
      art20_3: 'Медицинские услуги',
      art20: 'Внутренние освобождения',
    },
  },
  de: {
    invoice: 'RECHNUNG',
    invoiceNumber: 'Nr.',
    date: 'Datum',
    from: 'Von',
    billTo: 'An',
    nif: 'USt-ID',
    description: 'Beschreibung',
    quantity: 'Menge',
    unitPrice: 'Preis',
    retention: 'Einb.',
    vat: 'MwSt',
    total: 'Gesamt',
    subtotal: 'Zwischensumme',
    vatAmount: 'MwSt',
    retentionAmount: 'Einbehalt',
    paymentDetails: 'Zahlungsdetails',
    iban: 'IBAN',
    swift: 'SWIFT',
    bank: 'Bank',
    accountHolder: 'Inhaber',
    reference: 'Ref',
    vatExemption: 'MwSt-Befreiung',
    notes: 'Anmerkungen',
    thankYou: 'Vielen Dank',
    phone: 'Tel',
    email: 'Email',
    address: 'Adresse',
    vatExemptions: {
      art21: 'Warenexport',
      art21_2: 'Exportbezogene Dienstleistungen',
      art22: 'Exportgleichgestellte Umsatze',
      art25: 'Innergemeinschaftliche Lieferungen',
      art69: 'Dienstleistungen ausserhalb Spaniens',
      art20_18: 'Finanzgeschafte',
      art20_9: 'Bildungsdienstleistungen',
      art20_3: 'Gesundheitsdienstleistungen',
      art20: 'Inlandische Befreiungen',
    },
  },
  fr: {
    invoice: 'FACTURE',
    invoiceNumber: 'N',
    date: 'Date',
    from: 'De',
    billTo: 'A',
    nif: 'TVA',
    description: 'Description',
    quantity: 'Qte',
    unitPrice: 'Prix',
    retention: 'Ret.',
    vat: 'TVA',
    total: 'Total',
    subtotal: 'Sous-total',
    vatAmount: 'TVA',
    retentionAmount: 'Retenue',
    paymentDetails: 'Coordonnees bancaires',
    iban: 'IBAN',
    swift: 'SWIFT',
    bank: 'Banque',
    accountHolder: 'Titulaire',
    reference: 'Ref',
    vatExemption: 'Exoneration TVA',
    notes: 'Notes',
    thankYou: 'Merci pour votre confiance',
    phone: 'Tel',
    email: 'Email',
    address: 'Adresse',
    vatExemptions: {
      art21: 'Exportation de biens',
      art21_2: 'Services lies aux exportations',
      art22: 'Operations assimilees aux exportations',
      art25: 'Livraisons intracommunautaires',
      art69: 'Services aux entreprises hors Espagne',
      art20_18: 'Operations financieres',
      art20_9: 'Services d\'enseignement',
      art20_3: 'Services de sante',
      art20: 'Exonerations interieures',
    },
  },
}

// Monochrome color palette matching the site
const colors = {
  black: '#0a0a0a',
  dark: '#171717',
  gray: '#737373',
  lightGray: '#a3a3a3',
  border: '#e5e5e5',
  light: '#f5f5f5',
  white: '#ffffff',
}

// Minimalist Icons as SVG components
const IconCalendar = () => (
  <Svg width="12" height="12" viewBox="0 0 24 24">
    <Path
      d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z"
      stroke={colors.gray}
      strokeWidth="2"
      fill="none"
    />
  </Svg>
)

const IconUser = () => (
  <Svg width="12" height="12" viewBox="0 0 24 24">
    <Circle cx="12" cy="8" r="4" stroke={colors.gray} strokeWidth="2" fill="none" />
    <Path
      d="M20 21a8 8 0 10-16 0"
      stroke={colors.gray}
      strokeWidth="2"
      fill="none"
    />
  </Svg>
)

const IconBuilding = () => (
  <Svg width="12" height="12" viewBox="0 0 24 24">
    <Path
      d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 13v.01M9 17v.01"
      stroke={colors.gray}
      strokeWidth="2"
      fill="none"
    />
  </Svg>
)

const IconMail = () => (
  <Svg width="10" height="10" viewBox="0 0 24 24">
    <Path
      d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
      stroke={colors.gray}
      strokeWidth="2"
      fill="none"
    />
    <Path d="M22 6l-10 7L2 6" stroke={colors.gray} strokeWidth="2" fill="none" />
  </Svg>
)

const IconPhone = () => (
  <Svg width="10" height="10" viewBox="0 0 24 24">
    <Path
      d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
      stroke={colors.gray}
      strokeWidth="2"
      fill="none"
    />
  </Svg>
)

const IconCreditCard = () => (
  <Svg width="12" height="12" viewBox="0 0 24 24">
    <Path
      d="M1 10h22M3 4h18a2 2 0 012 2v12a2 2 0 01-2 2H3a2 2 0 01-2-2V6a2 2 0 012-2z"
      stroke={colors.gray}
      strokeWidth="2"
      fill="none"
    />
  </Svg>
)

const IconHash = () => (
  <Svg width="14" height="14" viewBox="0 0 24 24">
    <Line x1="4" y1="9" x2="20" y2="9" stroke={colors.dark} strokeWidth="2" />
    <Line x1="4" y1="15" x2="20" y2="15" stroke={colors.dark} strokeWidth="2" />
    <Line x1="10" y1="3" x2="8" y2="21" stroke={colors.dark} strokeWidth="2" />
    <Line x1="16" y1="3" x2="14" y2="21" stroke={colors.dark} strokeWidth="2" />
  </Svg>
)

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Inter',
    fontSize: 9,
    padding: 40,
    paddingBottom: 70,
    backgroundColor: colors.white,
    color: colors.dark,
  },
  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 40,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  brandSection: {
    flex: 1,
  },
  brandName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.black,
    marginBottom: 4,
  },
  brandTagline: {
    fontSize: 8,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  invoiceSection: {
    alignItems: 'flex-end',
  },
  invoiceLabel: {
    fontSize: 8,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 2,
    marginBottom: 4,
  },
  invoiceNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.black,
    flexDirection: 'row',
    alignItems: 'center',
  },
  invoiceNumberText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.black,
  },
  // Meta info
  metaSection: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 30,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 20,
  },
  metaIcon: {
    marginRight: 6,
  },
  metaText: {
    fontSize: 9,
    color: colors.dark,
  },
  // Parties section
  partiesSection: {
    flexDirection: 'row',
    marginBottom: 35,
    gap: 30,
  },
  partyBox: {
    flex: 1,
  },
  partyBoxHighlight: {
    flex: 1,
    backgroundColor: colors.light,
    padding: 16,
    borderRadius: 6,
  },
  partyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  partyIcon: {
    marginRight: 6,
  },
  partyLabel: {
    fontSize: 7,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1.5,
  },
  partyName: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.black,
    marginBottom: 6,
  },
  partyDetail: {
    fontSize: 8,
    color: colors.gray,
    marginBottom: 2,
    lineHeight: 1.4,
  },
  partyContact: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  partyContactIcon: {
    marginRight: 4,
  },
  partyContactText: {
    fontSize: 8,
    color: colors.gray,
  },
  // Table
  table: {
    marginBottom: 25,
  },
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: colors.black,
    borderRadius: 4,
  },
  tableHeaderCell: {
    fontSize: 7,
    fontWeight: 'bold',
    color: colors.white,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableRowAlt: {
    backgroundColor: colors.light,
  },
  tableCell: {
    fontSize: 9,
    color: colors.dark,
  },
  tableCellMuted: {
    fontSize: 9,
    color: colors.gray,
  },
  tableCellBold: {
    fontSize: 9,
    color: colors.black,
    fontWeight: 'bold',
  },
  // Column widths
  colDesc: { width: '42%' },
  colQty: { width: '8%', textAlign: 'center' },
  colPrice: { width: '15%', textAlign: 'right' },
  colRet: { width: '10%', textAlign: 'center' },
  colVat: { width: '10%', textAlign: 'center' },
  colTotal: { width: '15%', textAlign: 'right' },
  // Totals
  totalsWrapper: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 30,
  },
  totalsBox: {
    width: 200,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    paddingHorizontal: 0,
  },
  totalRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  totalLabel: {
    fontSize: 9,
    color: colors.gray,
  },
  totalValue: {
    fontSize: 9,
    color: colors.dark,
  },
  totalValueNegative: {
    fontSize: 9,
    color: '#dc2626',
  },
  grandTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: colors.black,
    borderRadius: 4,
    marginTop: 8,
  },
  grandTotalLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.white,
  },
  grandTotalValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.white,
  },
  // Payment section
  paymentSection: {
    marginBottom: 20,
  },
  paymentBox: {
    backgroundColor: colors.light,
    padding: 16,
    borderRadius: 6,
  },
  paymentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  paymentIcon: {
    marginRight: 8,
  },
  paymentTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    color: colors.dark,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  paymentGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  paymentItem: {
    width: '50%',
    marginBottom: 8,
  },
  paymentLabel: {
    fontSize: 7,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  paymentValue: {
    fontSize: 9,
    color: colors.dark,
  },
  // Note
  noteBox: {
    padding: 12,
    backgroundColor: '#fef3c7',
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: '#f59e0b',
    marginBottom: 20,
  },
  noteText: {
    fontSize: 8,
    color: '#92400e',
  },
  notesBox: {
    padding: 12,
    backgroundColor: colors.light,
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: colors.gray,
    marginBottom: 12,
  },
  notesTitle: {
    fontSize: 7,
    color: colors.gray,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  notesText: {
    fontSize: 8,
    color: colors.dark,
  },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  footerText: {
    fontSize: 8,
    color: colors.gray,
  },
  footerThank: {
    fontSize: 9,
    color: colors.dark,
    fontWeight: 'bold',
  },
  // Decorative
  decorLine: {
    height: 3,
    backgroundColor: colors.black,
    marginBottom: 30,
    borderRadius: 2,
  },
})

interface InvoiceDocumentProps {
  invoice: Invoice
  language: InvoiceLanguage
}

const formatDate = (dateString: string, language: InvoiceLanguage): string => {
  const date = new Date(dateString)
  const locales: Record<InvoiceLanguage, string> = {
    es: 'es-ES',
    en: 'en-US',
    ru: 'ru-RU',
    de: 'de-DE',
    fr: 'fr-FR',
  }
  return date.toLocaleDateString(locales[language], {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount)
}

export const InvoiceDocument: React.FC<InvoiceDocumentProps> = ({ invoice, language }) => {
  const t = translations[language]
  const hasRetention = invoice.totalWithholding > 0
  const exemptionReason = invoice.vatExemptionReason ? VAT_EXEMPTION_REASONS.find(r => r.value === invoice.vatExemptionReason) : undefined
  const exemptionLabel = invoice.vatExemptionReason ? t.vatExemptions[invoice.vatExemptionReason] : undefined

  // Country names are only available for es/en/ru — fall back to English for other PDF languages
  const addressLang: SupportedLanguage = language === 'es' || language === 'ru' ? language : 'en'
  // Build the display address from structured fields, falling back to the legacy single-line field
  const buildAddress = (party: {
    street?: string
    city?: string
    postalCode?: string
    countryCode?: string
    address?: string
  }): string =>
    formatAddress(
      {
        street: party.street,
        city: party.city,
        postalCode: party.postalCode,
        countryCode: party.countryCode,
      },
      addressLang
    ) || party.address || ''
  const senderAddress = buildAddress(invoice.sender)
  const recipientAddress = buildAddress(invoice.recipient)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Decorative line */}
        <View style={styles.decorLine} />

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.brandSection}>
            <Text style={styles.brandName}>{invoice.sender.name || 'Company'}</Text>
            <Text style={styles.brandTagline}>{invoice.sender.nif && `${t.nif}: ${invoice.sender.nif}`}</Text>
          </View>
          <View style={styles.invoiceSection}>
            <Text style={styles.invoiceLabel}>{t.invoice}</Text>
            <View style={styles.invoiceNumber}>
              <IconHash />
              <Text style={styles.invoiceNumberText}>{invoice.invoiceNumber}</Text>
            </View>
          </View>
        </View>

        {/* Meta - Date */}
        <View style={styles.metaSection}>
          <View style={styles.metaItem}>
            <View style={styles.metaIcon}>
              <IconCalendar />
            </View>
            <Text style={styles.metaText}>{formatDate(invoice.date, language)}</Text>
          </View>
        </View>

        {/* Parties */}
        <View style={styles.partiesSection}>
          {/* Sender */}
          <View style={styles.partyBox}>
            <View style={styles.partyHeader}>
              <View style={styles.partyIcon}>
                <IconBuilding />
              </View>
              <Text style={styles.partyLabel}>{t.from}</Text>
            </View>
            <Text style={styles.partyName}>{invoice.sender.name}</Text>
            {invoice.sender.nif && (
              <Text style={styles.partyDetail}>{t.nif}: {invoice.sender.nif}</Text>
            )}
            {senderAddress && (
              <Text style={styles.partyDetail}>{senderAddress}</Text>
            )}
            {invoice.sender.email && (
              <View style={styles.partyContact}>
                <View style={styles.partyContactIcon}>
                  <IconMail />
                </View>
                <Text style={styles.partyContactText}>{invoice.sender.email}</Text>
              </View>
            )}
            {invoice.sender.phone && (
              <View style={styles.partyContact}>
                <View style={styles.partyContactIcon}>
                  <IconPhone />
                </View>
                <Text style={styles.partyContactText}>{invoice.sender.phone}</Text>
              </View>
            )}
          </View>

          {/* Recipient */}
          <View style={styles.partyBoxHighlight}>
            <View style={styles.partyHeader}>
              <View style={styles.partyIcon}>
                <IconUser />
              </View>
              <Text style={styles.partyLabel}>{t.billTo}</Text>
            </View>
            <Text style={styles.partyName}>{invoice.recipient.name}</Text>
            {invoice.recipient.nif && (
              <Text style={styles.partyDetail}>{t.nif}: {invoice.recipient.nif}</Text>
            )}
            {recipientAddress && (
              <Text style={styles.partyDetail}>{recipientAddress}</Text>
            )}
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          {/* Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.colDesc]}>{t.description}</Text>
            <Text style={[styles.tableHeaderCell, styles.colQty]}>{t.quantity}</Text>
            <Text style={[styles.tableHeaderCell, styles.colPrice]}>{t.unitPrice}</Text>
            <Text style={[styles.tableHeaderCell, styles.colRet]}>{t.retention}</Text>
            <Text style={[styles.tableHeaderCell, styles.colVat]}>{t.vat}</Text>
            <Text style={[styles.tableHeaderCell, styles.colTotal]}>{t.total}</Text>
          </View>

          {/* Rows */}
          {invoice.items.map((item, idx) => (
            <View key={item.id} style={idx % 2 === 1 ? [styles.tableRow, styles.tableRowAlt] : styles.tableRow}>
              <Text style={[styles.tableCell, styles.colDesc]}>{item.description || '-'}</Text>
              <Text style={[styles.tableCellMuted, styles.colQty]}>{item.quantity}</Text>
              <Text style={[styles.tableCellMuted, styles.colPrice]}>{formatCurrency(item.priceWithoutVat)}</Text>
              <Text style={[styles.tableCellMuted, styles.colRet]}>{item.withholdingRate}%</Text>
              <Text style={[styles.tableCellMuted, styles.colVat]}>{item.vatRate}%</Text>
              <Text style={[styles.tableCellBold, styles.colTotal]}>{formatCurrency(item.subtotalWithVat)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsWrapper}>
          <View style={styles.totalsBox}>
            <View style={[styles.totalRow, styles.totalRowBorder]}>
              <Text style={styles.totalLabel}>{t.subtotal}</Text>
              <Text style={styles.totalValue}>{formatCurrency(invoice.baseAmount)}</Text>
            </View>
            {hasRetention && (
              <View style={[styles.totalRow, styles.totalRowBorder]}>
                <Text style={styles.totalLabel}>{t.retentionAmount}</Text>
                <Text style={styles.totalValueNegative}>-{formatCurrency(invoice.totalWithholding)}</Text>
              </View>
            )}
            <View style={[styles.totalRow, styles.totalRowBorder]}>
              <Text style={styles.totalLabel}>{t.vatAmount}</Text>
              <Text style={styles.totalValue}>{formatCurrency(invoice.totalVat)}</Text>
            </View>
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>{t.total}</Text>
              <Text style={styles.grandTotalValue}>{formatCurrency(invoice.totalAmount)}</Text>
            </View>
          </View>
        </View>

        {/* VAT Exemption Note */}
        {exemptionReason && exemptionLabel && (
          <View style={styles.noteBox}>
            <Text style={styles.noteText}>{t.vatExemption}: {exemptionReason.article} — {exemptionLabel}</Text>
          </View>
        )}

        {/* Payment Details */}
        {invoice.sender.paymentDetails && (invoice.sender.paymentDetails.iban || invoice.sender.paymentDetails.bankName) && (
          <View style={styles.paymentSection}>
            <View style={styles.paymentBox}>
              <View style={styles.paymentHeader}>
                <View style={styles.paymentIcon}>
                  <IconCreditCard />
                </View>
                <Text style={styles.paymentTitle}>{t.paymentDetails}</Text>
              </View>
              <View style={styles.paymentGrid}>
                {invoice.sender.paymentDetails.iban && (
                  <View style={styles.paymentItem}>
                    <Text style={styles.paymentLabel}>{t.iban}</Text>
                    <Text style={styles.paymentValue}>{invoice.sender.paymentDetails.iban}</Text>
                  </View>
                )}
                {invoice.sender.paymentDetails.swift && (
                  <View style={styles.paymentItem}>
                    <Text style={styles.paymentLabel}>{t.swift}</Text>
                    <Text style={styles.paymentValue}>{invoice.sender.paymentDetails.swift}</Text>
                  </View>
                )}
                {invoice.sender.paymentDetails.bankName && (
                  <View style={styles.paymentItem}>
                    <Text style={styles.paymentLabel}>{t.bank}</Text>
                    <Text style={styles.paymentValue}>{invoice.sender.paymentDetails.bankName}</Text>
                  </View>
                )}
                {invoice.sender.paymentDetails.accountHolder && (
                  <View style={styles.paymentItem}>
                    <Text style={styles.paymentLabel}>{t.accountHolder}</Text>
                    <Text style={styles.paymentValue}>{invoice.sender.paymentDetails.accountHolder}</Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        )}

        {/* Notes */}
        {invoice.notes && (
          <View style={styles.notesBox}>
            <Text style={styles.notesTitle}>{t.notes}</Text>
            <Text style={styles.notesText}>{invoice.notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerThank}>{t.thankYou}</Text>
          <Text style={styles.footerText}>
            {invoice.sender.email || ''}{invoice.sender.email && invoice.sender.phone ? ' | ' : ''}{invoice.sender.phone || ''}
          </Text>
        </View>
      </Page>
    </Document>
  )
}

export async function generateInvoiceBlobUrl(invoice: Invoice, language: InvoiceLanguage = 'es'): Promise<string> {
  const blob = await pdf(<InvoiceDocument invoice={invoice} language={language} />).toBlob()
  return URL.createObjectURL(blob)
}

export async function generateInvoicePDF(invoice: Invoice, language: InvoiceLanguage = 'es'): Promise<void> {
  const url = await generateInvoiceBlobUrl(invoice, language)
  const link = document.createElement('a')
  link.href = url
  link.download = `factura-${invoice.invoiceNumber}.pdf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
